import { action, computed } from '@ember/object';
import { classNames, tagName } from '@ember-decorators/component';
import { guidFor } from '@ember/object/internals';
import { isPresent } from '@ember/utils';
import { observes } from '@ember-decorators/object';
import { or } from '@ember/object/computed';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@ember/component';
import IsAnimatedMixin from '../../mixins/component/is-animated';
import classic from 'ember-classic-decorator';
import styles from './onboarding-popover.module.scss';

@classic
@tagName('')
@classNames(styles.component)
export default class SharedOnboardingPopover extends Component.extend(IsAnimatedMixin) {
  styles = styles;

  @service popSequencer;

  transitions = null;

  @computed('transitions.sortedStepTransitions.length', 'transitions.currentStep')
  get hasNext() {
    let hasNext = false;
    if (this.transitions) {
      hasNext = isPresent(this.transitions.pickNext(this.idx));
    }
    return hasNext;
  }

  @computed('buttonLabel', 'hasNext')
  get buttonText() {
    return this.hasNext ? 'Next Tip' : this.buttonLabel || 'Done';
  }

  @or('isDisabled', 'isTest') _isDisabled;

  /*
   * Observers are often over-used by new Ember developers. Observers are used
   * heavily within the Ember framework itself, but for most problems Ember
   * app developers face, computed properties are the appropriate solution.
   *
   * Below an observer is used for showing the popover because it is more
   * stable since the property is updated synchronously and then scheduled
   * once. A computed property was not consistent enough, often causing 2
   * popovers showing at the same time.
   *
   */

  // eslint-disable-next-line ember/no-observers
  @observes('_isDisabled', 'transitions.currentStep', 'idx')
  isShownChanged() {
    // eslint-disable-next-line ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-runloop
    scheduleOnce('afterRender', () => {
      this.set('isShown', !this._isDisabled && this.transitions?.currentStep === this.idx);
    });
  }

  init() {
    super.init(...arguments);
    if (!this.idx) {
      this.set('idx', this._idxToNumber(guidFor(this)));
    }
    if (!this._isDisabled) {
      this.popSequencer.register(this);
    }
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    this.popSequencer.remove(this);
  }

  @action
  transitionToNext(evt) {
    if (this.transitions) {
      let to = this.transitions.pickNext();
      this.send('transitionTo', to, evt);
    }
  }

  @action
  transitionTo(to, evt) {
    this.popSequencer.remove(this);
    this.transitions.activate(to);
    if (this.onTransition) {
      this.onTransition(evt);
    }
  }

  @action
  dismiss(evt) {
    this.transitions.sortedStepTransitions.invoke('onTransition', evt);
  }

  _idxToNumber(idx) {
    return +`${idx}`.replace(/\D+/g, '');
  }
}
